import { useEffect } from "react";

/**
 * Custom hook to implement infinite scroll functionality. It triggers a loadMore function 
 * when the user scrolls to the bottom (or, optionally, the top) of the container.
 * 
 * @param loadMore - Function to load more items.
 * @param hasMore - Boolean indicating if there are more items to load.
 * @param isLoading - Boolean indicating if items are currently being loaded.
 * @param rootRef - Ref to the scrollable container.
 * @param fromTop - Boolean indicating if loading should be triggered from the top (otherwise from the bottom).
 * @param threshold - The threshold in pixels from the top of the container to trigger loading more items.
 *  
 * @returns {void}
 */
export function useInfiniteScroll(
  loadMore: () => Promise<void> | void,
  hasMore: boolean,
  isLoading: boolean,
  rootRef: React.RefObject<HTMLElement | null>, 
  fromTop: boolean = false,
  threshold = 20,
): void {
  useEffect(() => {
    const pane = rootRef.current;
    if (!pane) return;

    const onScroll = () => {
      if (fromTop) {
        // Trigger loadMore when scrolled to the top
        if (
          (pane.scrollTop <= threshold) && 
          hasMore && !isLoading) {
          loadMore();
        }
      } else {
        // Trigger loadMore when scrolled to the bottom
        const scrollHeight = pane.scrollHeight;
        const clientHeight = pane.clientHeight;
        const scrollTop = pane.scrollTop;

        if (
          ((scrollHeight - clientHeight - scrollTop) <= threshold) && 
          hasMore && !isLoading) {
          loadMore();
        }
      }
    };

    pane.addEventListener("scroll", onScroll, { passive: true });
    return () => pane.removeEventListener("scroll", onScroll);
  }, [rootRef, threshold, hasMore, isLoading, loadMore, fromTop]);
}
